import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TIME_FORMAT } from 'src/app/core/ccomx-date-formats';
import {
  LogActionEnum,
  LogActionLabelDescription,
  LogActionLabels,
} from 'src/app/shared/enums/log-action.enum';
import { ILogChangeBreakdownItem } from 'src/app/shared/main-table-module/main-table-module-specific-template/table-module-templates/cells/log-change-breakdown/log-change-breakdown.model';
import { IGenericLogFieldChange } from 'src/app/shared/models/generic-log.model';

interface ITranslatedLogFieldChange extends IGenericLogFieldChange {
  translationKey: string;
}

interface INextOfKinLogChangeGroup {
  number: number;
  changes: ILogChangeBreakdownItem[];
}

type LogChangeItem =
  | { type: 'change'; change: ITranslatedLogFieldChange }
  | { type: 'nextOfKinGroup'; group: INextOfKinLogChangeGroup };

const NEXT_OF_KIN_ACTIONS = new Set<string>([
  LogActionEnum.NextOfKinCreated,
  LogActionEnum.NextOfKinUpdated,
  LogActionEnum.NextOfKinDeleted,
]);

@Component({
  selector: 'app-generic-log-template',
  templateUrl: './generic-log-template.component.html',
  styleUrls: ['./generic-log-template.component.scss'],
  standalone: false,
})
export class GenericLogTemplateComponent implements OnChanges {
  @Input() columnName: string;
  @Input() element: any;
  @Input() statusColor: string;
  @Input() tableColor: string;

  logActionEnum = LogActionEnum;
  logActionLabels: any = LogActionLabels;
  logActionLabelDescription: any = LogActionLabelDescription;
  timeFormat = TIME_FORMAT;
  logChangeItems: LogChangeItem[] = [];
  nextOfKinFieldChanges: ILogChangeBreakdownItem[] = [];
  actionTranslationKey = '';
  actionDescriptionTranslationKey = '';

  constructor() {}

  get isNextOfKinAction(): boolean {
    return NEXT_OF_KIN_ACTIONS.has(this.element?.action);
  }

  get nextOfKinSummaryTranslationKey(): string {
    return this.logActionLabelDescription[this.element?.action] ?? '';
  }

  get nextOfKinSubjectName(): string {
    return this.element?.subjectName?.trim() || '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.element) {
      return;
    }

    this.refreshActionLabels();
    this.refreshLogChangeItems();
  }

  private refreshActionLabels(): void {
    this.actionTranslationKey =
      this.logActionLabels[this.element?.action] ?? this.element?.action ?? '';
    this.actionDescriptionTranslationKey =
      this.element?.actionDescription ?? this.logActionLabelDescription[this.element?.action] ?? '';
  }

  private refreshLogChangeItems(): void {
    const fieldChanges = (this.element?.fieldsChanged || []).map(
      (fieldChange: IGenericLogFieldChange) => this.addTranslationKey(fieldChange)
    );
    this.logChangeItems = this.groupNextOfKinFieldChanges(fieldChanges);
    this.nextOfKinFieldChanges = this.logChangeItems
      .filter(
        (item): item is { type: 'nextOfKinGroup'; group: INextOfKinLogChangeGroup } =>
          item.type === 'nextOfKinGroup'
      )
      .flatMap((item) => item.group.changes);
  }

  private addTranslationKey(fieldChange: IGenericLogFieldChange): ITranslatedLogFieldChange {
    let mappedFieldChange = fieldChange;
    let translationKey = 'changedTo';

    if (this.element.action === LogActionEnum.Created) {
      translationKey = 'hasBeenAssignedAValueOf';
    }
    if (
      this.element.action === LogActionEnum.Edited ||
      this.element.action === LogActionEnum.Updated
    ) {
      translationKey = 'hasBeenUpdated';
    }
    if (
      fieldChange.oldValue != null &&
      fieldChange.oldValue !== '' &&
      (fieldChange.value === null || fieldChange.value === '')
    ) {
      translationKey = 'hasBeenCleared';
    }
    if (fieldChange.oldValue === null && fieldChange.value !== null && fieldChange.value !== '') {
      translationKey = 'hasBeenSetTo';
    }
    if (this.hasCustomTranslation(fieldChange.fieldName)) {
      translationKey = '';
    }
    if (
      (fieldChange.fieldName === 'expirationDate' && fieldChange.value === null) ||
      (fieldChange.fieldName === 'revisionDates' && fieldChange.value === '')
    ) {
      translationKey = 'hasBeenRemoved';
    }
    if (fieldChange.fieldName === 'signature') {
      translationKey = this.getSignatureTranslationKey(fieldChange);
      mappedFieldChange = { ...fieldChange, value: null };
    }
    if (fieldChange.fieldName === 'UserDefinedCertificateNumber') {
      mappedFieldChange = { ...mappedFieldChange, fieldName: 'customCertificateNumber' };
    }

    return { ...mappedFieldChange, translationKey };
  }

  private groupNextOfKinFieldChanges(fieldChanges: ITranslatedLogFieldChange[]): LogChangeItem[] {
    const groups = new Map<number, INextOfKinLogChangeGroup>();
    const nonNextOfKinItems: LogChangeItem[] = [];
    const nextOfKinGroupItems: LogChangeItem[] = [];

    fieldChanges.forEach((change) => {
      const number = this.getNextOfKinNumber(change);
      if (number === undefined) {
        nonNextOfKinItems.push({ type: 'change', change });
        return;
      }

      let group = groups.get(number);
      if (!group) {
        group = { number, changes: [] };
        groups.set(number, group);
        nextOfKinGroupItems.push({ type: 'nextOfKinGroup', group });
      }
      group.changes.push(this.toNextOfKinBreakdownItem(change));
    });

    return [...nonNextOfKinItems, ...nextOfKinGroupItems];
  }

  private getNextOfKinNumber(fieldChange: ITranslatedLogFieldChange): number | undefined {
    if (!this.isNextOfKinFieldChange(fieldChange)) {
      return undefined;
    }

    const number = fieldChange.translationParams?.number;
    return typeof number === 'number' ? number : undefined;
  }

  private getCompactNextOfKinFieldName(fieldName: string): string {
    const fieldNameWithoutPrefix = fieldName.replace(/^nextOfKin/, '');
    if (fieldNameWithoutPrefix === 'IsMainContact') {
      return 'mainContact';
    }

    return `${fieldNameWithoutPrefix.charAt(0).toLowerCase()}${fieldNameWithoutPrefix.slice(1)}`;
  }

  private toNextOfKinBreakdownItem(change: ITranslatedLogFieldChange): ILogChangeBreakdownItem {
    const hasValue = change.value !== null && change.value !== '';

    return {
      fieldName: this.getCompactNextOfKinFieldName(change.fieldName),
      value: change.value,
      translationKey: hasValue ? 'hasBeenSetTo' : 'hasBeenCleared',
    };
  }

  private getSignatureTranslationKey(fieldChange: IGenericLogFieldChange): string {
    const hadSignature = fieldChange.oldValue !== null && fieldChange.oldValue !== undefined;
    const hasSignature = fieldChange.value !== null && fieldChange.value !== undefined;

    if (!hadSignature && hasSignature) {
      return 'hasBeenAdded';
    }
    if (hadSignature && !hasSignature) {
      return 'hasBeenCleared';
    }

    return 'hasBeenChanged';
  }

  private hasCustomTranslation(fieldName: string): boolean {
    return [
      'attachmentsAdded',
      'attachmentsRemoved',
      'positionsWithAccessAdded',
      'positionsWithAccessRemoved',
      'positionGroupsWithAccessAdded',
      'positionGroupsWithAccessRemoved',
      'responsibleForPositionsAdded',
      'responsibleForPositionsRemoved',
    ].includes(fieldName);
  }

  private isNextOfKinFieldChange(fieldChange: IGenericLogFieldChange): boolean {
    return fieldChange.fieldName.startsWith('nextOfKin');
  }
}
